/**
 * ChannelChip — Roomgrid channel chip.
 *
 * Small 20px pill identifying a notification channel, colored with the
 * Roomgrid semantic triplets per the handoff mapping:
 *
 *   Email → info (teal-weak)   SMS → violet      WhatsApp → ok
 *   Telegram → accent outline  Web Push → warn   App Push → neutral
 *
 * Telegram uses the outline variant of the accent (transparent bg,
 * accent border + text) so it doesn't duplicate the Email/info chip.
 */

import React from "react";
import { Box, useTheme } from "@mui/material";

type ChannelKey =
  | "email"
  | "sms"
  | "whatsapp"
  | "telegram"
  | "webpush"
  | "apppush";

const LABELS: Record<ChannelKey, string> = {
  email: "Email",
  sms: "SMS",
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  webpush: "Web Push",
  apppush: "App Push",
};

const normalise = (value: string): ChannelKey | null => {
  const k = value.trim().toLowerCase().replace(/[\s_-]/g, "");
  if (k in LABELS) return k as ChannelKey;
  if (k === "mail") return "email";
  if (k === "wa") return "whatsapp";
  if (k === "push") return "apppush";
  return null;
};

export interface ChannelChipProps {
  channel: string;
  /** Override the display label (defaults to the canonical channel name). */
  label?: string;
  sx?: object;
}

export const ChannelChip: React.FC<ChannelChipProps> = ({
  channel,
  label,
  sx,
}) => {
  const theme = useTheme();
  const rg = theme.custom.rg;
  const key = normalise(channel);

  const styles = ((): { bg: string; fg: string; bd: string } => {
    switch (key) {
      case "email":
        return { bg: rg.info.bg, fg: rg.info.fg, bd: "transparent" };
      case "sms":
        return { bg: rg.violet.bg, fg: rg.violet.fg, bd: "transparent" };
      case "whatsapp":
        return { bg: rg.ok.bg, fg: rg.ok.fg, bd: "transparent" };
      case "telegram":
        return { bg: "transparent", fg: rg.accent, bd: rg.accent };
      case "webpush":
        return { bg: rg.warn.bg, fg: rg.warn.fg, bd: "transparent" };
      case "apppush":
      default:
        return { bg: rg.neutral.bg, fg: rg.neutral.fg, bd: "transparent" };
    }
  })();

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: 20,
        px: "8px",
        borderRadius: "99px",
        border: `1px solid ${styles.bd}`,
        backgroundColor: styles.bg,
        color: styles.fg,
        fontSize: "10.5px",
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: "nowrap",
        ...sx,
      }}
    >
      {label ?? (key ? LABELS[key] : channel)}
    </Box>
  );
};

export default ChannelChip;
